/**
 * REST client. SPEC.md §26, §35.
 *
 * REST covers the three things a socket cannot do yet: create a table, join one,
 * and prove who you are before a socket is opened. Once attached, everything
 * else is realtime. §15.4
 *
 * Failures always surface as an ApiError carrying the shared ErrorCode, with
 * copy taken from ERROR_MESSAGES — the UI never sees a status line, a stack or a
 * server string. §35
 */

import {
  ERROR_CODES,
  messageForErrorCode,
  type CreateSessionBody,
  type ErrorCode,
  type JoinSessionBody,
  type PlayerCredentials,
  type SessionHistory,
  type SessionSnapshot,
  type SessionStatus,
} from '@card-counter/shared';
import { readPlayerToken } from './storage';

const API_BASE = '/api';

/** Thrown by every function in this module. `code` drives the UI's copy. §35 */
export class ApiError extends Error {
  readonly code: ErrorCode;

  constructor(code: ErrorCode, message: string = messageForErrorCode(code)) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * Anything already carries safe copy or it doesn't get shown. Used by the toast
 * and inline-error paths so no raw exception text can reach a player. §35
 */
export function errorMessageOf(err: unknown): string {
  if (isApiError(err)) return err.message;
  if (typeof err === 'object' && err !== null) {
    const candidate = err as { code?: unknown; message?: unknown };
    if (typeof candidate.code === 'string' && isErrorCode(candidate.code)) {
      return messageForErrorCode(candidate.code);
    }
  }
  return messageForErrorCode('INTERNAL');
}

export function errorCodeOf(err: unknown): ErrorCode {
  if (isApiError(err)) return err.code;
  if (typeof err === 'object' && err !== null) {
    const candidate = (err as { code?: unknown }).code;
    if (typeof candidate === 'string' && isErrorCode(candidate)) return candidate;
  }
  return 'INTERNAL';
}

function isErrorCode(value: string): value is ErrorCode {
  return (ERROR_CODES as readonly string[]).includes(value);
}

/**
 * The join form needs to know a table exists, and whether it is locked, before
 * asking for a name. §6.3, §34.4
 *
 * Not in the shared package: only the credential, snapshot and history shapes
 * are contracted there, so this projection is asserted rather than validated.
 */
export interface SessionLookup {
  sessionId: string;
  joinCode: string;
  status: SessionStatus;
  locked: boolean;
  playerCount: number;
  /** Which game this is, so the join screen says so up front. §4.6 */
  drinkingMode: boolean;
  /**
   * Characters already at the table, so the picker can grey them out rather
   * than offer one the join would then refuse. Faces, not identities: no name,
   * seat or score comes with them. §46, §27
   */
  takenAvatarKeys: string[];
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  /** When set, the stored token for this session is sent as a bearer. §27 */
  sessionId?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, sessionId, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (sessionId) {
    const token = readPlayerToken(sessionId);
    // A join code is not a credential; the opaque player token is. §27, §39
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // No ErrorCode describes "the network is gone", so this keeps the code
    // generic and supplies copy that fits the situation.
    throw new ApiError('INTERNAL', "We can't reach the table right now. Check your connection.");
  }

  if (!response.ok) throw await toApiError(response);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** The wire only ever carries `{ code, message }`. §35 */
async function toApiError(response: Response): Promise<ApiError> {
  try {
    const payload: unknown = await response.json();
    if (typeof payload === 'object' && payload !== null) {
      const code = (payload as { code?: unknown }).code;
      if (typeof code === 'string' && isErrorCode(code)) return new ApiError(code);
    }
  } catch {
    /* Non-JSON body (a proxy error page, say) — fall through. */
  }
  return new ApiError(response.status === 404 ? 'SESSION_NOT_FOUND' : 'INTERNAL');
}

/**
 * Accepts both `{ key: value }` and a bare payload. §26 fixes the endpoints but
 * not their envelopes, and a snapshot arriving unwrapped must not break a
 * reconnect.
 */
function unwrap<T>(payload: unknown, key: string): T {
  if (typeof payload === 'object' && payload !== null && key in payload) {
    return (payload as Record<string, T>)[key]!;
  }
  return payload as T;
}

// --- Endpoints (§26) ---------------------------------------------------------

/** POST /api/sessions — creates a table; the creator becomes host. §6.1 */
export function createSession(
  body: CreateSessionBody,
  signal?: AbortSignal,
): Promise<PlayerCredentials> {
  return request<PlayerCredentials>('/sessions', { method: 'POST', body, signal });
}

/** GET /api/sessions/:joinCode — does this table exist, and is it open? §6.3 */
export function lookupSession(joinCode: string, signal?: AbortSignal): Promise<SessionLookup> {
  return request<SessionLookup>(`/sessions/${encodeURIComponent(joinCode)}`, { signal });
}

/** POST /api/sessions/:joinCode/join — name plus avatar, nothing else. §6.3 */
export function joinSession(
  joinCode: string,
  body: JoinSessionBody,
  signal?: AbortSignal,
): Promise<PlayerCredentials> {
  return request<PlayerCredentials>(`/sessions/${encodeURIComponent(joinCode)}/join`, {
    method: 'POST',
    body,
    signal,
  });
}

/**
 * POST /api/sessions/:sessionId/reconnect — restores an existing identity from
 * the stored token. Must never create a second player row. §6.4
 */
export function reconnectSession(
  sessionId: string,
  playerToken: string,
  signal?: AbortSignal,
): Promise<PlayerCredentials> {
  return request<PlayerCredentials>(`/sessions/${encodeURIComponent(sessionId)}/reconnect`, {
    method: 'POST',
    body: { playerToken },
    signal,
  });
}

/**
 * GET /api/sessions/:sessionId/snapshot — the authoritative state, used when the
 * socket is not up yet. Never cached as authoritative by the service worker.
 * §15.5, §17.1
 */
export async function fetchSnapshot(
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionSnapshot> {
  const payload = await request<unknown>(`/sessions/${encodeURIComponent(sessionId)}/snapshot`, {
    sessionId,
    signal,
  });
  return unwrap<SessionSnapshot>(payload, 'snapshot');
}

/** GET /api/sessions/:sessionId/history — full round-by-round history. §13.2 */
export async function fetchHistory(
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionHistory> {
  const payload = await request<unknown>(`/sessions/${encodeURIComponent(sessionId)}/history`, {
    sessionId,
    signal,
  });
  return unwrap<SessionHistory>(payload, 'history');
}
